"use client";
import { jsx, jsxs } from "react/jsx-runtime";
import { createElement } from "react";
import { useState, useRef } from "react";
import cx from "classnames";
import SSRIcon from "@ingka/ssr-icon";
import Button from "@ingka/button";
import HyperLink from "@ingka/hyperlink";
import closeIcon from "@ingka/ssr-icon/paths/cross-small";
import incorrectIcon from "@ingka/ssr-icon/paths/incorrect";
import warningIcon from "@ingka/ssr-icon/paths/warning-triangle";
import checkMarkIcon from "@ingka/ssr-icon/paths/checkmark-circle";
import informationIcon from "@ingka/ssr-icon/paths/information-circle";
import InlineMessageHeading from "./InlineMessageHeading.mjs";
const icons = {
  informative: informationIcon,
  positive: checkMarkIcon,
  cautionary: warningIcon,
  negative: incorrectIcon
};
const InlineMessage = (props) => {
  const {
    className,
    headingLevel = "6",
    actions,
    prefix = "",
    link,
    newWindow,
    linkLabel,
    body,
    ssrIcon,
    title,
    labelCloseButton,
    labelMessageType,
    subtle,
    variant = "informative",
    dismissable,
    onDismissClick,
    collapseWhenClosed,
    children,
    ...other
  } = props;
  const wrapperRef = useRef(null);
  const isInformative = variant === "informative";
  const hideIcon = isInformative && !ssrIcon;
  const [isMessageClosed, setMessageClose] = useState(false);
  const [wrapperHeight, setWrapperHeight] = useState();
  const [collapsed, setCollapsed] = useState(false);
  const closeClick = () => {
    requestAnimationFrame(function() {
      if (wrapperRef && wrapperRef.current) {
        setWrapperHeight(wrapperRef.current.scrollHeight);
      }
      requestAnimationFrame(function() {
        setWrapperHeight(0);
      });
    });
    setMessageClose(true);
  };
  const handleTransitionEnd = (e) => {
    if (e.propertyName !== "height") return;
    setCollapsed(true);
    if (onDismissClick) onDismissClick(e);
  };
  const baseClass = `${prefix}inline-message`;
  const classes = cx(
    baseClass,
    `${baseClass}--${variant}`,
    isMessageClosed && `${baseClass}--closed`,
    hideIcon && `${baseClass}--icon-hidden`,
    subtle && `${baseClass}--subtle`,
    collapseWhenClosed && collapsed && `${baseClass}--collapsed`,
    dismissable && `${baseClass}--dismissable`,
    className
  );
  const shouldDisplayLink = link && linkLabel;
  return /* @__PURE__ */ jsxs(
    "div",
    {
      "aria-hidden": isMessageClosed ? "true" : "false",
      className: classes,
      ref: wrapperRef,
      style: { height: wrapperHeight },
      onTransitionEnd: handleTransitionEnd,
      ...other,
      children: [
        !hideIcon && /* @__PURE__ */ jsx(
          SSRIcon,
          {
            className: `${baseClass}__icon`,
            paths: ssrIcon ? ssrIcon : icons[variant],
            prefix,
            ...labelMessageType && {
              ariaHidden: false,
              role: "img",
              "aria-label": labelMessageType
            }
          }
        ),
        /* @__PURE__ */ jsxs("div", { className: `${baseClass}__content`, children: [
          title && !subtle && /* @__PURE__ */ jsx(
            InlineMessageHeading,
            {
              headingLevel,
              className: `${baseClass}__title`,
              children: title
            }
          ),
          (body || shouldDisplayLink) && /* @__PURE__ */ jsxs("p", { className: `${baseClass}__body`, children: [
            body,
            " ",
            shouldDisplayLink && /* @__PURE__ */ jsx(
              HyperLink,
              {
                prefix,
                url: link,
                text: linkLabel,
                newWindow
              }
            )
          ] }),
          children,
          !subtle && actions && /* @__PURE__ */ jsx("div", { className: `${baseClass}__actions`, children: actions.map((action, i) => {
            return /* @__PURE__ */ createElement(
              Button,
              {
                ...action,
                type: "tertiary",
                size: "small",
                key: i,
                prefix
              }
            );
          }) })
        ] }),
        dismissable && /* @__PURE__ */ jsx(
          Button,
          {
            prefix,
            type: "tertiary",
            size: "xsmall",
            iconOnly: true,
            ssrIcon: closeIcon,
            onClick: closeClick,
            className: `${baseClass}__close-button`,
            text: labelCloseButton
          }
        )
      ]
    }
  );
};
var InlineMessage_default = InlineMessage;
export {
  InlineMessage_default as default
};
